import React, { useEffect, useState } from "react";
import { FileText, Palette, Save, Sparkles, Undo2 } from "lucide-react";
import { openDocumentFile, practiceApi } from "../lib/practiceApi";
import { PlanBanner, usePlan } from "./PlanBanner";

const FIELDS = [["accent", "Accento"], ["ink", "Testo"], ["muted", "Grigio"], ["soft", "Tenue"], ["paper", "Carta"]];

export function DocumentPalette({ onError, onDone }) {
  const [data, setData] = useState(null);
  const [paletteId, setPaletteId] = useState("");
  const [custom, setCustom] = useState({});
  const [busy, setBusy] = useState("");
  const plan = usePlan(onError);

  useEffect(() => {
    practiceApi("/settings/palette").then(result => {
      setData(result);
      setPaletteId(result.current.palette_id);
      setCustom(result.current.custom || {});
    }).catch(err => onError(err.message));
  }, [onError]);

  if (!data) return <p className="muted" data-testid="palette-loading">Caricamento palette…</p>;

  const pro = data.pro !== false;
  const colors = { ...(data.presets.find(p => p.id === paletteId) || data.presets[0]), ...custom };

  async function save() {
    setBusy("save"); onError("");
    try {
      const result = await practiceApi("/settings/palette", { method: "PUT", body: JSON.stringify({ palette_id: paletteId, custom }) });
      setData(current => ({ ...current, current: result }));
      onDone("Palette salvata: i prossimi documenti useranno questi colori");
    } catch (err) { onError(err.message); } finally { setBusy(""); }
  }

  async function preview() {
    setBusy("preview"); onError("");
    const query = new URLSearchParams({ palette_id: paletteId, ...custom });
    try {
      await openDocumentFile(`/settings/document-preview?${query}`, "Anteprima documento.pdf", "pdf");
    } catch (err) { onError(err.message); } finally { setBusy(""); }
  }

  return <div className="palette-tab" data-testid="settings-palette">
    <p className="muted">I colori scelti vengono usati nei titoli, nelle fasce, nelle intestazioni di tabella e nei filetti di tutti i PDF e Word. Logo, timbro e filigrana restano le immagini caricate.</p>

    <PlanBanner plan={plan} onError={onError} />

    {!pro && <button type="button" className="ghost" data-testid="palette-preview-standard" style={{ marginTop: 14 }}
      onClick={() => openDocumentFile("/settings/document-preview?plan=standard", "Anteprima documento.pdf", "pdf").catch(err => onError(err.message))}>
      <FileText size={15} />Anteprima documento Standard
    </button>}

    <div className={pro ? "" : "palette-locked"} aria-hidden={!pro}>
    {data.suggested.available && <div className="palette-suggestion" data-testid="palette-suggestion">
      <div>
        <strong><Sparkles size={15} /> Proposta dal logo dello studio</strong>
        <small>Colori dominanti del logo, smorzati per la stampa.</small>
        <div className="palette-swatches">
          {data.suggested.swatches.map(hex => <i key={hex} style={{ background: hex }} title={hex} />)}
        </div>
      </div>
      <div className="palette-preview-strip">
        {FIELDS.map(([key]) => <i key={key} style={{ background: data.suggested.colors[key] }} title={data.suggested.colors[key]} />)}
      </div>
      <button type="button" className="ghost" data-testid="palette-use-suggestion"
        onClick={() => setCustom({ ...data.suggested.colors })}>Usa questi colori</button>
    </div>}

    <div className="palette-grid">
      {data.presets.map(preset => <button type="button" key={preset.id} data-testid={`palette-preset-${preset.id}`}
        className={paletteId === preset.id && !Object.keys(custom).length ? "palette-card active" : "palette-card"}
        onClick={() => { setPaletteId(preset.id); setCustom({}); }}>
        <span className="palette-card-head">{preset.name}</span>
        <span className="palette-preview-strip">
          {FIELDS.map(([key]) => <i key={key} style={{ background: preset[key] }} />)}
        </span>
        <small>{preset.note}</small>
      </button>)}
    </div>

    <h3>Ritocco manuale</h3>
    <div className="palette-fields">
      {FIELDS.map(([key, label]) => <label key={key}>{label}
        <span>
          <input type="color" data-testid={`palette-color-${key}`} value={key === "paper" ? "#FFFFFF" : colors[key]} disabled={key === "paper"}
            onChange={e => setCustom(c => ({ ...c, [key]: e.target.value.toUpperCase() }))} />
          <code>{key === "paper" ? "#FFFFFF" : colors[key]}</code>
        </span>
      </label>)}
    </div>

    <div className="settings-actions">
      <button className="primary" type="button" disabled={!!busy} data-testid="palette-save" onClick={save}>
        <Save size={15} />{busy === "save" ? "Salvataggio…" : "Salva palette"}
      </button>
      <button className="ghost" type="button" disabled={!!busy} data-testid="palette-preview" onClick={preview}>
        <FileText size={15} />{busy === "preview" ? "Generazione…" : "Anteprima documento"}
      </button>
      {!!Object.keys(custom).length && <button className="ghost" type="button" data-testid="palette-reset-custom"
        onClick={() => setCustom({})}><Undo2 size={15} />Annulla ritocchi</button>}
      <span className="muted" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}>
        <Palette size={13} />In uso: {data.current.palette_id === paletteId && !Object.keys(custom).length ? "questa palette" : "palette non ancora salvata"}
      </span>
    </div>
    </div>
  </div>;
}
